import { Box, Flex, Menu, Portal, Separator, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useState } from 'react';

import { MicLevelMeter } from '#features/media/MicLevelMeter';
import { SPEAKER_SELECTION_SUPPORTED } from '#features/media-window/audioRouting';

type DeviceKind = 'audioinput' | 'audiooutput';

type VoiceDevicePickerProps = {
  active: boolean;
  listDevices: (kind: DeviceKind) => Promise<MediaDeviceInfo[]>;
  onSelect: (kind: DeviceKind, deviceId: string) => void | Promise<void>;
  /**
   * Список открывается не только своей стрелкой: после выданного разрешения его раскрывает сама
   * кнопка микрофона — человек уже здесь, и следующий его вопрос «а тем ли микрофоном я говорю».
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Режим рации: микрофон открыт только пока держат пробел. */
  pushToTalk: boolean;
  onPushToTalkChange: (enabled: boolean) => void;
};

/**
 * Микрофон и динамик рядом с кнопкой мьюта — тот же набор, что в звонке 1:1: разговор один и тот
 * же, и чинить «меня не слышно» человек приходит в одно и то же место.
 *
 * Список запрашивается в момент открытия, а не заранее: до входа в комнату у браузера ещё нет
 * разрешения, и `enumerateDevices` вернул бы безымянные записи вида «Микрофон (2)». Динамика нет
 * там, где браузер не умеет `setSinkId`, — выбор, который ничего не переключает, хуже его
 * отсутствия. Всей кнопки нет на iOS: там маршрутом звука распоряжается система.
 */
export const VoiceDevicePicker = ({
  active,
  listDevices,
  onSelect,
  open,
  onOpenChange,
  pushToTalk,
  onPushToTalkChange,
}: VoiceDevicePickerProps) => {
  const { t } = useLingui();
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [activeIds, setActiveIds] = useState<Partial<Record<DeviceKind, string>>>({});
  /**
   * Своё состояние нужно и в неуправляемом случае: измерение уровня идёт, только пока список
   * открыт, а внешний `open` приходит не всегда.
   */
  const [selfOpen, setSelfOpen] = useState(false);
  const measuring = open ?? selfOpen;

  const load = () => {
    void listDevices('audioinput').then(setMics);
    if (SPEAKER_SELECTION_SUPPORTED) {
      void listDevices('audiooutput').then(setSpeakers);
    }
  };

  const section = (kind: DeviceKind, label: string, devices: MediaDeviceInfo[], empty: string) => (
    <>
      <Text textStyle="label" color="fg.muted" px={2.5} pt={1.5} pb={1}>
        {label}
      </Text>

      {devices.length === 0 ? (
        <Text textStyle="xs" color="fg.secondary" px={2.5} py={2}>
          {empty}
        </Text>
      ) : (
        devices.map((device) => (
          <Menu.Item
            key={`${kind}:${device.deviceId}`}
            value={`${kind}:${device.deviceId}`}
            onClick={() => {
              setActiveIds((prev) => ({ ...prev, [kind]: device.deviceId }));
              void onSelect(kind, device.deviceId);
            }}
          >
            <Flex align="center" gap={2} minW={0}>
              <Box w="15px" flex="none" color="purple.fg" lineHeight={0}>
                {device.deviceId === activeIds[kind] ? <CheckGlyph /> : null}
              </Box>
              <Text textStyle="xs" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {device.label || t`post:voice.widget.unnamedDevice`}
              </Text>
            </Flex>
          </Menu.Item>
        ))
      )}
    </>
  );

  return (
    <Menu.Root
      {...(open === undefined ? {} : { open })}
      onOpenChange={({ open: next }) => {
        setSelfOpen(next);
        if (next) {
          load();
        }
        onOpenChange?.(next);
      }}
    >
      <Menu.Trigger asChild>
        <Box
          as="button"
          w="26px"
          h="44px"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="lg"
          color={active ? 'status.live.fg' : 'fg.secondary'}
          aria-label={t`post:voice.widget.pickDevice`}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </Box>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          {/* Меню обязано лежать ВЫШЕ окна разговора, из которого оно открыто. Штатный слой меню —
              `dropdown` (1000), а плавающее окно стоит на `popover` (1500), поэтому список
              устройств уезжал под окно: видна оставалась только полоска, торчащая выше его верхней
              кромки, — выбрать микрофон было нечем. `skipNav` (1600) — ближайший слой над окном и
              по-прежнему ниже тостов (1700).

              Слой задаётся ИМЕННО на контенте, а не на позиционере: позиционеру z-index пишет
              инлайном zag (`zIndex: var(--z-index)`), а сам `--z-index` он копирует с первого
              ребёнка — то есть отсюда. Любая правка позиционера снаружи молча ничего не меняет. */}
          <Menu.Content
            zIndex="skipNav"
            minW="268px"
            bg="bg.card"
            borderRadius="xl"
            boxShadow="lg"
            p={1.5}
          >
            {section(
              'audioinput',
              t`post:voice.widget.micLabel`,
              mics,
              t`post:voice.widget.noMics`
            )}
            <MicLevelMeter enabled={measuring} deviceId={activeIds.audioinput} />
            {SPEAKER_SELECTION_SUPPORTED
              ? section(
                  'audiooutput',
                  t`post:voice.widget.speakerLabel`,
                  speakers,
                  t`post:voice.widget.noSpeakers`
                )
              : null}

            {/* Только там, где есть клавиатура: на телефоне пробела нет, и пункт был бы обманом.
                Тот же порог, по которому скрыта подсказка про пробел в самом окне. */}
            <Box display={{ base: 'none', md: 'block' }}>
              <Separator my={1.5} />
              <Menu.Item
                value="push-to-talk"
                closeOnSelect={false}
                onClick={() => onPushToTalkChange(!pushToTalk)}
              >
                <Flex align="center" gap={2} minW={0}>
                  <Box w="15px" flex="none" color="purple.fg" lineHeight={0}>
                    {pushToTalk ? <CheckGlyph /> : null}
                  </Box>
                  <Box minW={0}>
                    <Text textStyle="xs">{t`post:voice.widget.pushToTalkMode`}</Text>
                    <Text textStyle="2xs" color="fg.muted">
                      {pushToTalk
                        ? t`post:voice.widget.pushToTalkOn`
                        : t`post:voice.widget.pushToTalkOff`}
                    </Text>
                  </Box>
                </Flex>
              </Menu.Item>
            </Box>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

const CheckGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12l5 5L20 7" />
  </svg>
);
